import { GraduationCap, BookOpen, Users, Target, Clock, Award, Calendar, MapPin, Building2, Globe, Shield, ChevronRight, CheckCircle2, Briefcase, UserCheck, Download, Star, ArrowRight, Play, ExternalLink, Heart, Lightbulb, TrendingUp } from 'lucide-react';
import ProgramDetail from '@/components/ProgramDetail';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import ProgramsSidebar from '@/components/ProgramsSidebar';
import { motion } from 'framer-motion';

const Education = () => {
  const { t } = useLanguage();
  
  const educationDetails = [
    t('programs.education.details.history', 'The Education & Vocational Training program was established by Vision Jeunesse Nouvelle in 2005. It began as a response to the critical need for accessible education and skill development among Rwandan youth, especially those affected by the 1994 Genocide against the Tutsi.'),
    t('programs.education.details.initialFocus', 'Initially, the program focused on providing basic literacy and numeracy skills to out-of-school youth and adults. Over time, it expanded to include vocational training in various trades such as tailoring, carpentry, and hairdressing, responding to market demands.'),
    t('programs.education.details.partnerships', 'VJN has partnered with several organizations, including UNICEF, USAID, and the Rwandan Ministry of Education, to enhance the quality and reach of its educational initiatives. These partnerships have enabled the program to provide scholarships, educational materials, and modern training facilities.'),
    t('programs.education.details.impact', 'The program has successfully empowered thousands of youth, enabling them to secure employment, start their own businesses, and contribute to the economic development of their communities. It has also fostered a culture of continuous learning and innovation among participants.')
  ];

  const objectives = [
    t('programs.education.objectives.main', 'To provide quality education and vocational training that equips youth with essential skills for employment and entrepreneurship.'),
    t('programs.education.objectives.specific1', 'Enhance literacy and numeracy skills among out-of-school youth and adults.'),
    t('programs.education.objectives.specific2', 'Provide market-driven vocational training to improve employability and self-employment opportunities.'),
    t('programs.education.objectives.specific3', 'Foster critical thinking, creativity, and problem-solving skills through innovative learning methods.'),
    t('programs.education.objectives.specific4', 'Promote gender equality and inclusivity in education, ensuring equal opportunities for all.')
  ];

  const selectionCriteria = [
    {
      title: t('programs.education.criteria.vocational.title', 'Vocational Training Center'),
      criteria: [
        t('programs.education.criteria.vocational.age', 'Age: 16 years or above'),
        t('programs.education.criteria.vocational.education', 'Completed Primary School (P6) or above'),
        t('programs.education.criteria.vocational.literacy', 'Basic literacy skills'),
        t('programs.education.criteria.vocational.priority', 'Vulnerable youth prioritized'),
        t('programs.education.criteria.vocational.gender', 'Open to both genders')
      ]
    },
    {
      title: t('programs.education.criteria.instructor.title', 'Literacy Instructors'),
      criteria: [
        t('programs.education.criteria.instructor.residency', 'Residency in the literacy zone'),
        t('programs.education.criteria.instructor.age', 'Age: 21-50 years'),
        t('programs.education.criteria.instructor.education', 'A\' Level minimum'),
        t('programs.education.criteria.instructor.writing', 'Proficiency in writing'),
        t('programs.education.criteria.instructor.volunteer', 'Willingness to volunteer'),
        t('programs.education.criteria.instructor.languages', 'Basic English and French skills')
      ]
    },
    {
      title: t('programs.education.criteria.choir.title', 'Intama za Yezu Choir'),
      criteria: [
        t('programs.education.criteria.choir.age', 'Youth age range'),
        t('programs.education.criteria.choir.passion', 'Passion for music'),
        t('programs.education.criteria.choir.commitment', 'Commitment to regular rehearsals'),
        t('programs.education.criteria.choir.skills', 'Basic vocal skills'),
        t('programs.education.criteria.choir.faith', 'Catholic faith'),
        t('programs.education.criteria.choir.parish', 'Parish affiliation')
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Hidden on mobile, shown on PC */}
          <div className="hidden lg:block lg:w-1/5">
            <ProgramsSidebar />
          </div>

          {/* Main Content */}
          <div className="flex-1 lg:w-4/5">
            {/* Hero Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-3xl overflow-hidden mb-8"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-transparent"></div>
              
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full"></div>
                <div className="absolute top-32 right-20 w-24 h-24 bg-white rounded-full"></div>
                <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-white rounded-full"></div>
              </div>

              <div className="relative z-10 px-8 py-16 lg:py-20">
                <div className="max-w-4xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex items-center gap-3 mb-6"
                  >
                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                      <GraduationCap className="h-8 w-8 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Education Program</span>
                  </motion.div>

                  <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight"
                  >
                    {t('programs.education.title', 'Education & Vocational Training')}
                  </motion.h1>

                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl"
                  >
                    For over 16 years, we've trained 2,400+ youth in four essential trades: Automobile Mechanics, Welding, Hairdressing, and Tailoring. Our comprehensive vocational program creates sustainable employment opportunities and empowers communities across Rwanda.
                  </motion.p>

                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="flex flex-wrap gap-4"
                  >
                    <Link to="/volunteer">
                      <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                        <Users className="mr-2 h-5 w-5" />
                        Join Our Program
                      </Button>
                    </Link>
                    <a
                      href="/downloads/brochures/Education.pdf"
                      download
                      className="inline-flex items-center px-8 py-4 bg-white/20 text-white hover:bg-white/30 rounded-xl font-semibold text-lg transition-all duration-300 backdrop-blur-sm border border-white/30"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Brochure
                    </a>
                  </motion.div>
                </div>
              </div>
            </motion.section>

            {/* Impact Statistics Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact in Numbers</h2>
                <p className="text-gray-600 text-lg">Transforming lives through education and vocational training</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {[
                  { icon: <GraduationCap className="h-8 w-8" />, value: '2,400+', label: 'Students Trained', color: 'from-blue-500 to-blue-600' },
                  { icon: <Briefcase className="h-8 w-8" />, value: '4', label: 'Vocational Trades', color: 'from-green-500 to-green-600' },
                  { icon: <BookOpen className="h-8 w-8" />, value: '16+', label: 'Years of Service', color: 'from-purple-500 to-purple-600' },
                  { icon: <Target className="h-8 w-8" />, value: '85%', label: 'Employment Rate', color: 'from-orange-500 to-orange-600' },
                  { icon: <Users className="h-8 w-8" />, value: '150+', label: 'Students per Year', color: 'from-pink-500 to-pink-600' }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="text-center group"
                  >
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${stat.color} text-white mb-4 group-hover:shadow-lg transition-all duration-300`}>
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Program Overview Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Program Overview</h2>
                <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                  For over 16 years, we've been empowering youth through comprehensive vocational training in four key trades: Automobile Mechanics, Welding, Hairdressing, and Tailoring. Our program combines practical skills with essential life skills to create sustainable employment opportunities.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Program Details */}
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <GraduationCap className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Program History</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {t('programs.education.details.history', 'The Education & Vocational Training program was established by Vision Jeunesse Nouvelle in 2005. It began as a response to the critical need for accessible education and skill development among Rwandan youth, especially those affected by the 1994 Genocide against the Tutsi.')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Target className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Focus</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {t('programs.education.details.initialFocus', 'Initially, the program focused on providing basic literacy and numeracy skills to out-of-school youth and adults. Over time, it expanded to include vocational training in various trades such as tailoring, carpentry, and hairdressing, responding to market demands.')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Heart className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Partnerships</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {t('programs.education.details.partnerships', 'VJN has partnered with several organizations, including UNICEF, USAID, and the Rwandan Ministry of Education, to enhance the quality and reach of its educational initiatives. These partnerships have enabled the program to provide scholarships, educational materials, and modern training facilities.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Program Impact */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="h-6 w-6 text-blue-600 mr-2" />
                    Our Impact
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t('programs.education.details.impact', 'The program has successfully empowered thousands of youth, enabling them to secure employment, start their own businesses, and contribute to the economic development of their communities. It has also fostered a culture of continuous learning and innovation among participants.')}
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">2,400+ Students Trained</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">4 Vocational Trades</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">85% Employment Rate</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">150+ Students per Year</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">16+ Years of Service</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Objectives Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Program Objectives</h2>
                <p className="text-gray-600 text-lg">Our strategic goals for empowering youth through education</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {objectives.map((objective, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    className="flex items-start space-x-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {index === 0 ? 'Main Objective' : `Specific Goal ${index}`}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{objective}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Target Groups Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Target Groups</h2>
                <p className="text-gray-600 text-lg">Who we serve and support through our programs</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                {[
                  { 
                    title: 'Automobile Mechanics', 
                    description: 'Youth training in vehicle repair and maintenance',
                    icon: <Briefcase className="h-8 w-8" />,
                    color: 'from-blue-500 to-blue-600'
                  },
                  { 
                    title: 'Welding', 
                    description: 'Students learning metal fabrication skills',
                    icon: <Target className="h-8 w-8" />,
                    color: 'from-orange-500 to-orange-600'
                  },
                  { 
                    title: 'Hairdressing', 
                    description: 'Youth developing beauty and styling expertise',
                    icon: <Star className="h-8 w-8" />,
                    color: 'from-pink-500 to-pink-600'
                  },
                  { 
                    title: 'Tailoring', 
                    description: 'Students mastering garment design and sewing',
                    icon: <Heart className="h-8 w-8" />,
                    color: 'from-purple-500 to-purple-600'
                  },
                  { 
                    title: 'Literacy & Numeracy', 
                    description: 'Adults and youth improving basic skills',
                    icon: <BookOpen className="h-8 w-8" />,
                    color: 'from-green-500 to-green-600'
                  },
                  { 
                    title: 'Gold Youth', 
                    description: 'Young people in leadership development',
                    icon: <Users className="h-8 w-8" />,
                    color: 'from-indigo-500 to-indigo-600'
                  }
                ].map((group, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="text-center group"
                  >
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${group.color} text-white mb-4 group-hover:shadow-lg transition-all duration-300`}>
                      {group.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{group.title}</h3>
                    <p className="text-gray-600">{group.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Selection Criteria Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Selection Criteria</h2>
                <p className="text-gray-600 text-lg">Requirements and qualifications for different program components</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {selectionCriteria.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="text-center mb-6">
                      <div className="inline-flex p-4 bg-blue-600 rounded-2xl text-white mb-4">
                        {index === 0 && <GraduationCap className="h-8 w-8" />}
                        {index === 1 && <BookOpen className="h-8 w-8" />}
                        {index === 2 && <Users className="h-8 w-8" />}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{category.title}</h3>
                    </div>
                    
                    <ul className="space-y-4">
                      {category.criteria.map((criterion, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-0.5">
                            <CheckCircle2 className="h-4 w-4 text-white" />
                          </div>
                          <span className="text-gray-700 leading-relaxed">{criterion}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Context & Challenges Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Context & Challenges</h2>
                <p className="text-gray-600 text-lg">Understanding the educational landscape in Rwanda</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Lightbulb className="h-6 w-6 text-orange-600 mr-2" />
                      Current Challenges
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {t('programs.education.context.paragraph1', 'In Rwanda, the education sector faces critical barriers to providing quality, equitable learning opportunities. According to UNESCO\'s "Institute for Statistics 2022" report, Rwanda has 1,967,627 individuals who are illiterate, with females accounting for 1,164,074 of these totals. Local authorities report that the Rubavu District alone has approximately 47,000 illiterate residents spanning various age groups.')}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Target className="h-6 w-6 text-blue-600 mr-2" />
                      Our Response
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {t('programs.education.context.paragraph2', 'These challenges are compounded by limited infrastructure, lack of trained teachers, and cultural or social barriers, which further hinder educational progress. Additionally, economic constraints and displacement prevent families from prioritizing education, leading to high dropout rates, illiteracy, and a lack of vocational skills among young people.')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Call to Action Section */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-3xl overflow-hidden mb-8"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-transparent"></div>
              
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 right-10 w-32 h-32 bg-white rounded-full"></div>
                <div className="absolute bottom-10 left-10 w-24 h-24 bg-white rounded-full"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full"></div>
              </div>

              <div className="relative z-10 px-8 py-16 text-center">
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="text-4xl lg:text-5xl font-bold text-white mb-6"
                >
                  {t('programs.education.cta.title', 'Get Involved')}
                </motion.h2>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                  className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed"
                >
                  {t('programs.education.cta.description', 'Join us in our mission to provide quality education and vocational training to youth in Rwanda. Your support can make a difference.')}
                </motion.p>
                
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="flex flex-wrap justify-center gap-6"
                >
                  <Link to="/volunteer">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                      <Users className="mr-2 h-5 w-5" />
                      {t('programs.education.cta.volunteer', 'Volunteer With Us')}
                    </Button>
                  </Link>
                  <Link to="/donate">
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300">
                      <Heart className="mr-2 h-5 w-5" />
                      {t('programs.education.cta.donate', 'Support Our Programs')}
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Education;
